import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink, FileText } from 'lucide-react';
import { useToast } from '../../../src/contexts/ToastContext';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import { listarRecompensas, urlDoComprovante } from '../../../src/services/indicacoes';
import {
  RECOMPENSA_STATUS,
  ROTULO_TIPO_RECOMPENSA,
  SELO_CLIENTE,
  formatarDataHora,
  formatarMoeda,
} from '../../../src/lib/indicacoes/rotulos';
import { formatarDocumento } from '../../../src/lib/termoNegociacao';
import type { IndicacaoRecompensa, RecompensaStatus } from '../../../types';
import NegotiationDetailModal from '../components/NegotiationDetails/NegotiationDetailModal';
import RecompensaAcaoModal, { type AcaoRecompensa } from './RecompensaAcaoModal';
import Selo from './Selo';
import { CLASSE_CARTAO, CLASSE_INPUT } from './estilos';

type Filtro = RecompensaStatus | 'a_definir' | '';

const FILTROS: { id: Filtro; rotulo: string }[] = [
  { id: 'a_pagar', rotulo: 'A pagar' },
  { id: 'a_definir', rotulo: 'A definir (sem valor)' },
  { id: 'suspensa', rotulo: 'Suspensas' },
  { id: 'paga', rotulo: 'Pagas' },
  { id: 'cancelada', rotulo: 'Canceladas' },
  { id: '', rotulo: 'Todas' },
];

const BOTAO_ACAO =
  'text-xs font-semibold px-2 py-1 rounded-md border border-gray-200 dark:border-slate-700 hover:border-primary hover:text-primary';

const RecompensasTab: React.FC<{ versao: number; onMudou: () => void }> = ({ versao, onMudou }) => {
  const { addToast } = useToast();
  const [filtro, setFiltro] = useState<Filtro>('a_pagar');
  const [linhas, setLinhas] = useState<IndicacaoRecompensa[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [acao, setAcao] = useState<AcaoRecompensa | null>(null);
  const [negAberta, setNegAberta] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setLinhas(await listarRecompensas(filtro));
    } catch (e) {
      setErro((e as Error).message);
      setLinhas([]);
    } finally {
      setCarregando(false);
    }
  }, [filtro]);

  useEffect(() => {
    carregar();
  }, [carregar, versao]);

  // Abre a janela antes do await: o navegador bloqueia window.open fora do clique.
  const verComprovante = async (caminho: string) => {
    const janela = window.open('', '_blank');
    try {
      const url = await urlDoComprovante(caminho);
      if (janela) janela.location.href = url;
    } catch (e) {
      janela?.close();
      addToast((e as Error).message, 'error');
    }
  };

  return (
    <div className="space-y-3">
      <div className={`${CLASSE_CARTAO} p-3`}>
        <select className={`${CLASSE_INPUT} sm:w-64`} value={filtro} onChange={e => setFiltro(e.target.value as Filtro)}>
          {FILTROS.map(f => (
            <option key={f.id || 'todas'} value={f.id}>{f.rotulo}</option>
          ))}
        </select>
      </div>

      {erro && <p className="text-sm text-red-600">{erro}</p>}

      <div className={`${CLASSE_CARTAO} overflow-x-auto`}>
        {carregando ? (
          <LoadingSpinner label="Carregando recompensas..." />
        ) : linhas.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-12">Nenhuma recompensa neste filtro.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 dark:text-slate-400 border-b border-gray-100 dark:border-slate-700">
              <tr>
                <th className="text-left font-semibold px-4 py-2">Quem recebe</th>
                <th className="text-left font-semibold px-4 py-2">Indicado</th>
                <th className="text-left font-semibold px-4 py-2">Recompensa</th>
                <th className="text-left font-semibold px-4 py-2">Situação</th>
                <th className="text-left font-semibold px-4 py-2">Datas</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700/60">
              {linhas.map(r => {
                const est = RECOMPENSA_STATUS[r.status];
                const selo = r.tipo_indicador === 'cliente' ? SELO_CLIENTE.sim : SELO_CLIENTE.nao;
                const negId = r.negociacao_id;
                const caminho = r.comprovante_path;
                return (
                  <tr key={r.id}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900 dark:text-white">{r.indicador_nome}</p>
                        <Selo classe={selo.classe}>{selo.rotulo}</Selo>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        {r.indicador_cpf_cnpj ? formatarDocumento(r.indicador_cpf_cnpj) : 'sem CPF'} ·{' '}
                        {r.indicador_chave_pix ? `PIX ${r.indicador_chave_pix}` : 'sem PIX'}
                      </p>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-gray-700 dark:text-slate-200">{r.indicado_nome}</span>
                      {negId != null ? (
                        <button
                          onClick={() => setNegAberta(negId)}
                          className="ml-1 p-0.5 text-gray-400 hover:text-primary align-middle"
                          title="Abrir negociação"
                        >
                          <ExternalLink size={13} />
                        </button>
                      ) : (
                        <p className="text-[11px] text-red-600">negociação removida</p>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-700 dark:text-slate-200">
                      <p>{r.tipo_recompensa ? ROTULO_TIPO_RECOMPENSA[r.tipo_recompensa] : 'Tipo a definir'}</p>
                      <p className="font-bold">{formatarMoeda(r.valor)}</p>
                    </td>
                    <td className="px-4 py-2.5">
                      <Selo classe={est.classe}>{est.rotulo}</Selo>
                      {r.alerta && (
                        <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1 flex items-start gap-1" title={r.alerta}>
                          <AlertTriangle size={12} className="shrink-0 mt-0.5" /> {r.alerta}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-slate-300">
                      <p>Gerada {formatarDataHora(r.gerada_em)}</p>
                      {r.paga_em && (
                        <p>
                          Paga {formatarDataHora(r.paga_em)}
                          {r.paga_por_nome ? ` · ${r.paga_por_nome}` : ''}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap justify-end gap-1">
                        {(r.status === 'a_pagar' || r.status === 'suspensa') && (
                          <button className={BOTAO_ACAO} onClick={() => setAcao({ tipo: 'ajustar', rec: r })}>Ajustar</button>
                        )}
                        {r.status === 'a_pagar' && (
                          <button className={BOTAO_ACAO} onClick={() => setAcao({ tipo: 'pagar', rec: r })}>Pagar</button>
                        )}
                        {(r.status === 'a_pagar' || r.status === 'suspensa') && (
                          <button className={BOTAO_ACAO} onClick={() => setAcao({ tipo: 'cancelar', rec: r })}>Cancelar</button>
                        )}
                        {caminho && (
                          <button className={BOTAO_ACAO} onClick={() => verComprovante(caminho)}>
                            <FileText size={12} className="inline" /> Comprovante
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {acao && (
        <RecompensaAcaoModal
          acao={acao}
          onClose={() => setAcao(null)}
          onFeito={() => {
            setAcao(null);
            carregar();
            onMudou();
          }}
        />
      )}
      <NegotiationDetailModal
        negotiationId={negAberta}
        isOpen={negAberta != null}
        onClose={() => setNegAberta(null)}
        onUpdate={carregar}
      />
    </div>
  );
};

export default RecompensasTab;
